import { z } from "zod";
import { MediumLongString } from "./primitives";
import { FirebaseTimestamp } from "./record.firestore";

const OnboardingContentFeedbackSchema = z.enum(["neutral", "like", "heart", "dislike"]);

export type OnboardingContentFeedback = z.infer<typeof OnboardingContentFeedbackSchema>;

const OnboardingContentSchema = z.object({
    title: z.string(),
    coverImageUrl: z.string(),
    feedback: OnboardingContentFeedbackSchema,
    description: MediumLongString.optional(),
});

export const OnboardingBenefitContent = z
    .object({
        group: z.string(),
    })
    .extend(OnboardingContentSchema.shape);

export type OnboardingBenefitContent = z.infer<typeof OnboardingBenefitContent>;

const OnboardingRecommendationContentSchema = z
    .object({
        category: z.string(),
    })
    .extend(OnboardingContentSchema.shape);

export type OnboardingRecommendationContent = z.infer<
    typeof OnboardingRecommendationContentSchema
>;

export const OnboardingTopCitySchema = z.object({
    city: z.string(),
    country: z.string(),
    countryCode: z.string().optional(),
    googlePlaceId: z.string(),
    appLocale: z.string().optional().nullable(),
});

export const HasUsedConciergeForSchema = z.enum([
    "saveTime",
    "discoverNewExperiences",
    "accessBenefits",
    "somethingElse",
]);
export type HasUsedConciergeFor = z.infer<typeof HasUsedConciergeForSchema>;

const UpcomingDiningPlansAnswerSchema = z.enum(["yes", "no"]);
const UpcomingTravelPlansAnswerSchema = z.enum(["yes", "no", "some"]);
const HasUsedConciergeAnswerSchema = z.enum(["yes", "no", "maybe"]);
export type UpcomingDiningPlansAnswer = z.infer<typeof UpcomingDiningPlansAnswerSchema>;
export type UpcomingTravelPlansAnswer = z.infer<typeof UpcomingTravelPlansAnswerSchema>;
export type HasUsedConciergeAnswer = z.infer<typeof HasUsedConciergeAnswerSchema>;

export const HasUsedConciergeAnswerTextMapSchema: Record<
    HasUsedConciergeAnswer,
    string
> = {
    yes: "Has used concierge before.",
    maybe: "Not sure about previous concierge experience.",
    no: "Has not used concierge before.",
};

export const HasUsedConciergeForTextMapSchema: Record<HasUsedConciergeFor, string> = {
    saveTime: "Saving time on day-to-day life admin",
    discoverNewExperiences: "Discovering new experiences",
    accessBenefits: "Preferential access and benefits",
    somethingElse: "Something else",
};

export type OnboardingTopCity = z.infer<typeof OnboardingTopCitySchema>;

export const OnboardingSteps = z.union([
    z.literal("conciergeExperience"),
    z.literal("start"),
    z.literal("memberDetails"),
    z.literal("name"),
    z.literal("email"),
    z.literal("phone"),
    z.literal("cities"),
    z.literal("placingRequests"),
    z.literal("diningPlans"),
    z.literal("travelPlans"),
    z.literal("surveyPermission"),
    z.literal("legal"),
]);
export type OnboardingSteps = z.infer<typeof OnboardingSteps>;

type OnboardingStepOrderHelper<T extends "start" | "legal"> = readonly [
    T,
    ...Array<Exclude<OnboardingSteps, T | "diningPlans">>,
    "diningPlans",
];

export type OnboardingStepOrder =
    | OnboardingStepOrderHelper<"start">
    | OnboardingStepOrderHelper<"legal">;

export const OnboardingPreferencesSchema = z.object({
    greetingsName: z.string().optional().nullable(),
    topCities: z.array(OnboardingTopCitySchema).optional().nullable(),
    benefits: z.array(OnboardingBenefitContent).optional().nullable(),
    recommendations: z
        .array(OnboardingRecommendationContentSchema)
        .optional()
        .nullable(),
    anyUpcomingTravelPlans: UpcomingTravelPlansAnswerSchema.optional().nullable(),
    upcomingTravelQuery: z.string().optional().nullable(),
    anyUpcomingDiningPlans: UpcomingDiningPlansAnswerSchema.optional().nullable(),
    upcomingDiningQuery: z.string().optional().nullable(),
    hasUsedConcierge: HasUsedConciergeAnswerSchema.optional().nullable(),
    hasUsedConciergeFor: HasUsedConciergeForSchema.optional().nullable().or(z.string()),
});

export type OnboardingPreferences = z.infer<typeof OnboardingPreferencesSchema>;

export const OnboardingPreferencesDocSchema = OnboardingPreferencesSchema.and(
    z.object({
        createdAt: FirebaseTimestamp,
        updatedAt: FirebaseTimestamp.nullable().optional(),
    }),
);
export type OnboardingPreferencesDoc = z.infer<typeof OnboardingPreferencesDocSchema>;
